"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { FormProvider, useForm } from "react-hook-form";
import useSWRInfinite from "swr/infinite";
import { FormAutoSubmit } from "../components/Form/AutoSubmit/AutoSubmit";
import { useApiFetcher } from "./useApiFetcher";

/**
 * @template {import("react-hook-form").FieldValues} T
 * @callback UseSearchInfiniteGenerateUrl
 * @param {T} fields
 * @param {number} key
 * @param {any} previousPage
 * @returns {string | [string, ...any] | null}
 */

// #region useSearchInfinite
/**
 * @template {import("react-hook-form").FieldValues} T
 * @typedef {object} UseSearchInfiniteProps
 * @prop {T} defaultValues
 * @prop {T} initialValues
 * @prop {UseSearchInfiniteGenerateUrl<T>} generateUrl
 * @prop {boolean} useRouterUrl
 * @prop {boolean} autoSubmit
 * @prop {boolean} keepPreviousData
 * @prop {any[]} [fallbackData]
 * @prop {string} pathname
 * @prop {number} [refreshInterval]
 * @prop {boolean} [revalidateAll]
 * @prop {import("swr/infinite").SWRInfiniteConfiguration<any>["onSuccess"]} [onSuccess]
 */
/**
 * Un hook permettant d'initialiser la valeur du contexte SearchInfiniteContext
 * @template {import("react-hook-form").FieldValues} TFieldValues
 * @param {UseSearchInfiniteProps<TFieldValues>} props
 * @returns {SearchInfiniteContextValue<TFieldValues>}
 */
export function useSearchInfinite({
  defaultValues,
  initialValues,
  generateUrl,
  useRouterUrl,
  autoSubmit,
  keepPreviousData,
  fallbackData,
  pathname,
  refreshInterval = 0,
  revalidateAll = false,
  onSuccess,
}) {
  const { apiFetcher } = useApiFetcher();

  const [submittedFields, setSubmittedFields] = useState(
    /** @type {TFieldValues} */ (initialValues),
  );

  // #region form
  /** @type {import("react-hook-form").UseFormReturn<TFieldValues>} */
  const form = useForm({
    values: initialValues, // updates the form values when the router values change
    defaultValues:
      /** @type {import("react-hook-form").DefaultValues<TFieldValues>} */ (
        defaultValues
      ),
  });
  const { setValue, handleSubmit } = form;

  // #region getKey
  const getKey = useCallback(
    /** @type {(key: any, previousPage: any) => string | string[] | null} */
    (key, previousPage) => {
      const url = generateUrl(submittedFields, key, previousPage);
      return url;
    },
    [generateUrl, submittedFields],
  );

  // #region swrInfiniteResponse
  const swrInfiniteResponse = useSWRInfinite(getKey, apiFetcher, {
    dedupingInterval: 0,
    refreshInterval,
    keepPreviousData,
    revalidateOnMount: true,
    revalidateFirstPage: false,
    revalidateAll,
    fallbackData,
    onSuccess,
  });
  const { setSize } = swrInfiniteResponse;

  // #region search
  const search = useCallback(
    /**
     * @param {TFieldValues} values
     */
    (values) => {
      if (useRouterUrl) {
        const searchParams = new URLSearchParams(window.location.search);

        for (const key in values) {
          const value = values[key];
          const defaultValue = defaultValues[key];

          let isDefault = false;
          if (Array.isArray(value)) {
            if (
              Array.isArray(defaultValue) &&
              value.length === defaultValue.length
            ) {
              isDefault = value.every(
                /**
                 * @param {any} v
                 */
                (v) => defaultValue.includes(v),
              );
            }
          } else {
            isDefault = value === defaultValue;
          }

          if (isDefault) {
            searchParams.delete(key);
            searchParams.delete(`${key}[]`);
          } else {
            if (Array.isArray(value)) {
              searchParams.delete(`${key}[]`);
              value.forEach(
                /**
                 * @param {any} v
                 */
                (v) => searchParams.append(`${key}[]`, v),
              );
            } else if (typeof value === "object" && value !== null) {
              // do nothing for objects
            } else {
              searchParams.set(key, String(value));
            }
          }
        }

        window.history.pushState(
          undefined,
          "",
          `${pathname.split("?")[0]}?${searchParams.toString()}`,
        );
      }
      setSubmittedFields(values);
    },
    [useRouterUrl, pathname, defaultValues],
  );

  // #region submit
  const submit = useMemo(() => {
    return handleSubmit((values) => {
      search(values);
    });
  }, [handleSubmit, search]);

  // #region appliedFilters
  const appliedFilters = useMemo(() => {
    // for each key in submitted fields
    return Object.keys(submittedFields).reduce((acc, key) => {
      // if the submitted value is different from the default value
      if (Array.isArray(submittedFields[key])) {
        const isDifferent = submittedFields[key].some(
          (value) => !defaultValues[key].includes(value),
        );
        if (isDifferent) {
          // @ts-expect-error
          acc = [...acc, key];
        }
      } else if (submittedFields[key] !== defaultValues[key]) {
        // @ts-expect-error
        acc = [...acc, key];
      }
      return acc;
    }, /** @type {import("react-hook-form").FieldPath<TFieldValues>[]} */ ([]));
  }, [defaultValues, submittedFields]);

  // #region reset
  const reset = useCallback(() => {
    Object.keys(defaultValues).forEach((key) => {
      // @ts-expect-error
      setValue(key, defaultValues[key], { shouldDirty: true });
    });
    if (autoSubmit === false) {
      search(defaultValues);
    }
  }, [defaultValues, autoSubmit, setValue, search]);

  // #region resetToSubmittedFields
  const resetToSubmittedFields = useCallback(() => {
    Object.keys(submittedFields).forEach((key) => {
      // @ts-expect-error
      setValue(key, submittedFields[key], { shouldDirty: true });
    });
    if (autoSubmit === false) {
      search(submittedFields);
    }
  }, [submittedFields, autoSubmit, setValue, search]);

  // #region contextValue
  const searchInfiniteContextValue = useMemo(() => {
    /** @type {SearchInfiniteContextValue<TFieldValues>} */
    const value = {
      swrInfiniteResponse,
      reset,
      appliedFilters,
      submit,
      form,
      autoSubmit,
      submittedFields,
      setSize,
      handleSubmit,
      resetToSubmittedFields,
      trigger: form.trigger,
    };
    return value;
  }, [
    swrInfiniteResponse,
    reset,
    appliedFilters,
    submit,
    form,
    autoSubmit,
    submittedFields,
    setSize,
    handleSubmit,
    resetToSubmittedFields,
  ]);

  return searchInfiniteContextValue;
}

// #region useSearchInfiniteContext
/**
 * @template {import("react-hook-form").FieldValues} [TFields=any]
 * @template [TResponse=any]
 * @typedef {SearchInfiniteContextValue<TFields, TResponse>} UseSearchInfiniteContext
 */
export function useSearchInfiniteContext() {
  return useContext(SearchInfiniteContext);
}

/**
 * @template {import("react-hook-form").FieldValues} TFieldValues
 * @template {any} [TResponse=any]
 * @typedef {object} SearchInfiniteContextValue
 * @prop {import("swr/infinite").SWRInfiniteResponse<import("./useApiFetcher").ApiFetcherReturn<TResponse>>} swrInfiniteResponse
 * @prop {() => void} reset
 * @prop {import("react-hook-form").FieldPath<TFieldValues>[]} appliedFilters
 * @prop {() => void} submit
 * @prop {any} handleSubmit
 * @prop {import("react-hook-form").UseFormReturn<TFieldValues>} form
 * @prop {boolean} autoSubmit
 * @prop {TFieldValues} submittedFields
 * @prop {(size: number | ((_size: number) => number)) => Promise<any[] | undefined>} setSize
 * @prop {() => void} resetToSubmittedFields
 * @prop {import("react-hook-form").UseFormTrigger<TFieldValues>} trigger
 */

/** @type {Partial<SearchInfiniteContextValue<any, any>>} */
const SEARCH_INFINITE_CONTEXT_DEFAULT_VALUE = {
  swrInfiniteResponse: undefined,
  reset: () => {},
  appliedFilters: [],
  submit: () => {},
  form: undefined,
  autoSubmit: false,
  submittedFields: {},
};

export const SearchInfiniteContext = createContext(
  /** @type {SearchInfiniteContextValue<any, any>} */ (
    SEARCH_INFINITE_CONTEXT_DEFAULT_VALUE
  ),
);

// #region SearchInfiniteProvider
/**
 * @typedef {object} Props
 * @prop {import("react").ReactNode} children
 */
/**
 * @param {Props & SearchInfiniteContextValue<any, any>} props
 */
export function SearchInfiniteProvider({
  form,
  appliedFilters,
  reset,
  submit,
  swrInfiniteResponse,
  autoSubmit,
  submittedFields,
  setSize,
  resetToSubmittedFields,
  trigger,
  children,
}) {
  const handleSubmit = useCallback(
    /** @type {import("react").FormEventHandler<HTMLDivElement>} */
    (e) => {
      e.preventDefault();
      submit();
    },
    [submit],
  );

  const value = useMemo(() => {
    /** @type {SearchInfiniteContextValue<any, any>} */
    const value = {
      appliedFilters,
      form,
      reset,
      submit,
      handleSubmit,
      swrInfiniteResponse,
      autoSubmit,
      submittedFields,
      setSize,
      resetToSubmittedFields,
      trigger,
    };
    return value;
  }, [
    appliedFilters,
    form,
    reset,
    submit,
    handleSubmit,
    swrInfiniteResponse,
    autoSubmit,
    submittedFields,
    setSize,
    resetToSubmittedFields,
    trigger,
  ]);

  return (
    <FormProvider {...form}>
      <SearchInfiniteContext.Provider value={value}>
        <FormAutoSubmit onSubmit={submit} isDisabled={!autoSubmit} />

        {children}
      </SearchInfiniteContext.Provider>
    </FormProvider>
  );
}
